/**
 * Rendering of dropped items and XP orbs, plus the shared "item model" builder
 * the first-person hand reuses.
 *
 *   const renderer = new ItemEntityRenderer(env, atlas, atlasTexture);
 *   scene.add(renderer.group);
 *   renderer.update(items.views(), xp.views(), alpha, camera);   // every frame
 *   renderer.models                                              // shared with FirstPersonHand
 *
 * Look is vanilla's ItemEntityRenderer:
 *  - items are extruded 1px-thick sprites (front/back quad + a side quad on every edge pixel that
 *    borders transparency), half a block wide; blocks are 0.25-scale 3D cubes (or their baked
 *    model: chest, bed); non-cube blocks (flowers, torches ...) are flat sprites of their tile.
 *  - spin `age/20 + bobOffs` rad, bob `sin(age/10 + bobOffs) * 0.1 + 0.1`, 2-5 offset copies for stacks.
 *  - XP orbs are camera-facing quads of experience_orb.png (4x4 sheet), tinted by the pulsing
 *    vanilla colour at half alpha.
 *
 * Lighting: one scalar `view.light` (0..1, sky*daylight and block light already folded in, like
 * MobView.light) through the engine's lightmap curve, shaded by vanilla's entity diffuse (two
 * lights, up 1.0 ... down 0.4), output in linear HDR to match the voxel material, and the same radial
 * fog from the shared env uniforms.
 */

import * as THREE from 'three';
import { syncGlint } from './glint';
import { ATLAS_COLS, CELL, GUTTER, TILE } from '../atlasLayout';
import {
  AIR, BED_FOOT_ID, BED_HEAD_ID, BLOCKS, DOOR_BOTTOM_TO_TOP, RenderShape,
} from '../blocks';
import { iconTint, MASKED_TINT_TILES } from '../biomeTint';
import { I, ITEM_BASE, type ItemName } from '../game/itemIds';
import type { ItemEntityView } from '../game/types';
import { drawItemIcon } from '../items';
import { isSpearId } from '../game/spear';
import { spriteOf } from '../game/itemdb';
import { modelQuads } from '../models';
import type { AtlasResult } from '../textures';
import type { XpOrbView } from '../entities/xp';
import { orbIconFor } from '../entities/xp';
import type { EnvUniforms } from './env';
import { LIGHTMAP_GLSL } from './lightmap';

// --- lit entity material -----------------------------------------------------------

const VERT = /* glsl */ `
in vec3 aTint;
in float aMask;
out vec2 vUv;
out vec3 vN;
out vec3 vTint;
out float vMask;
out vec3 vWorld;
void main() {
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
#ifdef HAND
  vN = normal; // item-model space: fixed face shading that does not swing with the camera
#else
  vN = mat3(modelMatrix) * normal;
#endif
  vUv = uv;
  vTint = aTint;
  vMask = aMask;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const FRAG = /* glsl */ `
precision highp float;
uniform sampler2D uMap;
uniform float uLight;
uniform float uBright;
uniform float uFogOn;
uniform float uAlpha;
uniform float uFlat;
uniform vec3 uColor;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform vec3 uSunGlow;
uniform vec3 uTrueSunDir;
uniform float uGamma;
uniform float uHandSky;
uniform float uHandBlock;
in vec2 vUv;
in vec3 vN;
in vec3 vTint;
in float vMask;
in vec3 vWorld;
out vec4 fragColor;

${LIGHTMAP_GLSL}

// vanilla Lighting.setupLevel: two lights, 0.6 weight over 0.4 ambient
const vec3 L0 = vec3(0.16222142, 0.81110711, -0.56777498);
const vec3 L1 = vec3(-0.16222142, 0.81110711, 0.56777498);

void main() {
  vec4 tex = texture(uMap, vUv);
  vec3 tint = vTint;
  float a = tex.a;
  // grass-side strip: only the alpha-marked texels take the biome tint (see atlasOverlays)
  if (vMask > 0.5) {
    tint = mix(vec3(1.0), vTint, clamp((1.0 - tex.a) * 2.007874, 0.0, 1.0));
    a = 1.0;
  }
#ifndef ORB
  if (a < 0.5) discard;
  vec3 N = normalize(vN);
  // vanilla lights sprite items flat (ITEMS_FLAT); only 3D block models take the two-light diffuse
  float shade = uFlat > 0.5 ? 1.0 : min(1.0, 0.4 + 0.6 * (max(0.0, dot(N, L0)) + max(0.0, dot(N, L1))));
#else
  float shade = 1.0;
#endif
#ifdef HAND
  // Held item / arm (vanilla ItemInHandRenderer): one lightmap sample at the
  // eye (sky already darkened for time of day, block light with its warm
  // tint), no shadows, no scene lights. Sprites are flat-lit; block models
  // get the fixed face shade: top 1.0, north/south 0.8, east/west 0.6, bottom 0.5.
  if (uFlat < 0.5) {
    vec3 an = abs(N);
    shade = an.y > 0.5 ? (N.y > 0.0 ? 1.0 : 0.5) : (an.z >= an.x ? 0.8 : 0.6);
  }
  vec3 lm = max(vec3(lightCurve(uHandSky)), blockLightTint(lightCurve(uHandBlock)));
  vec3 color = tex.rgb * tint * uColor * (shade * uBright * lm + 0.02);
#else
  float lc = lightCurve(uLight); // vanilla lightmap (floor + Brightness); uLight is already time-of-day scaled
  vec3 color = tex.rgb * tint * uColor * (shade * lc * uBright + 0.012);
#endif
  if (uFogOn > 0.5) {
    vec3 toCam = cameraPosition - vWorld;
    float fog = smoothstep(uFogNear, uFogFar, max(length(toCam.xz), abs(toCam.y) * 0.7));
    vec2 sunXZ = normalize(uTrueSunDir.xz + vec2(0.00001));
    float toward = max(dot(normalize(-toCam.xz + vec2(0.00001)), sunXZ), 0.0);
    color = mix(color, uFogColor + uSunGlow * (pow(toward, 4.0) + 0.12), fog);
  }
#ifdef ORB
  fragColor = vec4(color, tex.a * uAlpha);
#else
  fragColor = vec4(color, 1.0);
#endif
}
`;

/** Scene-referred brightness at light 1: a sunlit terrain top face lands near 1.25. */
const BRIGHT = 1.2;

export type LitKind = 'world' | 'hand' | 'orb';

/** Lightmap inputs for every first-person material (0..1; sky already scaled by time of day). Set by FirstPersonHand. */
export const HAND_LIGHT = { sky: { value: 1 }, block: { value: 0 } };

/** A lit entity material. `uLight` (and `uColor` for orbs) are set per mesh in onBeforeRender via `setMeshLight`. */
export function createLitMaterial(env: EnvUniforms, map: THREE.Texture | null, kind: LitKind, flat = false): THREE.ShaderMaterial {
  const m = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    glslVersion: THREE.GLSL3,
    uniforms: {
      uMap: { value: map },
      uLight: { value: 1 },
      uBright: { value: BRIGHT },
      uFogOn: { value: kind === 'hand' ? 0 : 1 },
      uAlpha: { value: 0.5 },
      uFlat: { value: flat ? 1 : 0 },
      uColor: { value: new THREE.Color(1, 1, 1) },
      uFogColor: env.uFogColor,
      uFogNear: env.uFogNear,
      uFogFar: env.uFogFar,
      uSunGlow: env.uSunGlow,
      uTrueSunDir: env.uTrueSunDir,
      uGamma: env.uGamma,
      uHandSky: HAND_LIGHT.sky,
      uHandBlock: HAND_LIGHT.block,
    },
    defines: kind === 'orb' ? { ORB: 1 } : kind === 'hand' ? { HAND: 1 } : {},
    transparent: kind !== 'world',
    depthWrite: kind !== 'orb',
  });
  return m;
}

interface LightData { light: number; r: number; g: number; b: number }

/** Make `mesh` upload its own light (and tint) into the shared material right before it draws. */
export function setMeshLight(mesh: THREE.Mesh, light: number, r = 1, g = 1, b = 1): void {
  let d = mesh.userData.lit as LightData | undefined;
  if (!d) {
    d = { light, r, g, b };
    mesh.userData.lit = d;
    const mat = mesh.material as THREE.ShaderMaterial;
    mesh.onBeforeRender = () => {
      const u = mat.uniforms;
      u.uLight.value = d!.light;
      (u.uColor.value as THREE.Color).setRGB(d!.r, d!.g, d!.b);
      mat.uniformsNeedUpdate = true;
    };
  }
  d.light = Math.min(1, Math.max(0, light)); d.r = r; d.g = g; d.b = b;
}

// --- geometry builders ----------------------------------------------------------------

interface Geo { pos: number[]; nor: number[]; uv: number[]; tint: number[]; mask: number[]; idx: number[] }
const newGeo = (): Geo => ({ pos: [], nor: [], uv: [], tint: [], mask: [], idx: [] });

/** Add a quad whose points run counter-clockwise seen from outside (normal `n`). */
function addQuad(
  g: Geo, p: ReadonlyArray<readonly [number, number, number]>, n: readonly [number, number, number],
  uv: ReadonlyArray<readonly [number, number]>, tint: readonly [number, number, number] = [1, 1, 1], mask = 0,
): void {
  const base = g.pos.length / 3;
  for (let i = 0; i < 4; i++) {
    g.pos.push(p[i][0], p[i][1], p[i][2]);
    g.nor.push(n[0], n[1], n[2]);
    g.uv.push(uv[i][0], uv[i][1]);
    g.tint.push(tint[0], tint[1], tint[2]);
    g.mask.push(mask);
  }
  g.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function finish(g: Geo): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(g.pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(g.nor, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(g.uv, 2));
  geo.setAttribute('aTint', new THREE.Float32BufferAttribute(g.tint, 3));
  geo.setAttribute('aMask', new THREE.Float32BufferAttribute(g.mask, 1));
  geo.setIndex(g.idx);
  geo.computeBoundingSphere();
  return geo;
}

const srgbToLinear = (c: number): number => Math.pow(c / 255, 2.2);

/** 0xRRGGBB sRGB -> linear tint triple. */
function tintTriple(t: number | null): [number, number, number] {
  if (t === null) return [1, 1, 1];
  return [srgbToLinear((t >> 16) & 255), srgbToLinear((t >> 8) & 255), srgbToLinear(t & 255)];
}

/** Vanilla's generated item model: the sprite extruded 1/16 thick, centred on the origin, 1 block wide. */
function spriteGeometry(canvas: HTMLCanvasElement): THREE.BufferGeometry {
  const N = canvas.width;
  const data = canvas.getContext('2d')!.getImageData(0, 0, N, canvas.height).data;
  const solid = (i: number, j: number): boolean => i >= 0 && j >= 0 && i < N && j < N && data[(j * N + i) * 4 + 3] > 0;
  const T = 1 / 32;
  const g = newGeo();
  // front (+Z) and back (-Z): the whole sprite, alpha-tested; the back reads mirrored like vanilla
  addQuad(g, [[-0.5, -0.5, T], [0.5, -0.5, T], [0.5, 0.5, T], [-0.5, 0.5, T]], [0, 0, 1], [[0, 0], [1, 0], [1, 1], [0, 1]]);
  addQuad(g, [[0.5, -0.5, -T], [-0.5, -0.5, -T], [-0.5, 0.5, -T], [0.5, 0.5, -T]], [0, 0, -1], [[1, 0], [0, 0], [0, 1], [1, 1]]);
  // one thin quad on every opaque pixel edge that borders transparency; each samples its pixel's centre
  for (let j = 0; j < N; j++) {
    for (let i = 0; i < N; i++) {
      if (!solid(i, j)) continue;
      const x0 = -0.5 + i / N, x1 = -0.5 + (i + 1) / N;
      const y1 = 0.5 - j / N, y0 = 0.5 - (j + 1) / N;
      const u = (i + 0.5) / N, v = 1 - (j + 0.5) / N;
      const uv = [[u, v], [u, v], [u, v], [u, v]] as const;
      if (!solid(i - 1, j)) addQuad(g, [[x0, y0, T], [x0, y1, T], [x0, y1, -T], [x0, y0, -T]], [-1, 0, 0], uv);
      if (!solid(i + 1, j)) addQuad(g, [[x1, y0, -T], [x1, y1, -T], [x1, y1, T], [x1, y0, T]], [1, 0, 0], uv);
      if (!solid(i, j - 1)) addQuad(g, [[x0, y1, -T], [x0, y1, T], [x1, y1, T], [x1, y1, -T]], [0, 1, 0], uv);
      if (!solid(i, j + 1)) addQuad(g, [[x0, y0, T], [x0, y0, -T], [x1, y0, -T], [x1, y0, T]], [0, -1, 0], uv);
    }
  }
  return finish(g);
}

/** Unit cube centred on the origin, faces textured from atlas tiles [+X,-X,+Y,-Y,+Z,-Z]. */
function cubeGeometry(id: number, atlas: AtlasResult): THREE.BufferGeometry {
  const def = BLOCKS[id];
  const g = newGeo();
  const x0 = -0.5, x1 = 0.5, y0 = -0.5, y1 = 0.5, z0 = -0.5, z1 = 0.5;
  // winding and u/v follow the mesher's block-model quads (see primedTnt.ts)
  const faces: Array<[ReadonlyArray<readonly [number, number, number]>, ReadonlyArray<readonly [number, number]>, readonly [number, number, number]]> = [
    [[[x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1]], [[1, 1], [0, 1], [0, 0], [1, 0]], [1, 0, 0]],
    [[[x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0]], [[0, 1], [1, 1], [1, 0], [0, 0]], [-1, 0, 0]],
    [[[x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0]], [[0, 1], [0, 0], [1, 0], [1, 1]], [0, 1, 0]],
    [[[x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1]], [[0, 0], [1, 0], [1, 1], [0, 1]], [0, -1, 0]],
    [[[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]], [[0, 1], [1, 1], [1, 0], [0, 0]], [0, 0, 1]],
    [[[x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0]], [[1, 1], [0, 1], [0, 0], [1, 0]], [0, 0, -1]],
  ];
  for (let f = 0; f < 6; f++) {
    const tile = def.tiles[f];
    const [p, uv, n] = faces[f];
    addQuad(g, p, n, uv.map(([u, v]) => atlasUv(atlas, tile, u, v)), tintTriple(iconTint(id, tile)), MASKED_TINT_TILES[tile] === 1 ? 1 : 0);
  }
  return finish(g);
}

function atlasUv(atlas: AtlasResult, tile: number, u: number, v: number): [number, number] {
  return [atlas.tileOriginU[tile] + u * atlas.tileSizeU, atlas.tileOriginV[tile] + v * atlas.tileSizeV];
}

/** Baked block-entity model (chest, bed) scaled to fit one block and centred on the origin. */
function modelGeometry(id: number, atlas: AtlasResult): THREE.BufferGeometry {
  const none = { px: AIR, nx: AIR, pz: AIR, nz: AIR };
  let quads;
  if (id === BED_FOOT_ID || id === BED_HEAD_ID) {
    const foot = modelQuads(BED_FOOT_ID, { ...none, px: BED_HEAD_ID });
    const head = modelQuads(BED_HEAD_ID, { ...none, nx: BED_FOOT_ID });
    quads = [...foot, ...head.map((q) => ({ ...q, pos: q.pos.map((v, i) => (i % 3 === 0 ? v + 1 : v)) }))];
  } else quads = [...modelQuads(id, none)];
  const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
  for (const q of quads) for (let i = 0; i < 12; i++) { lo[i % 3] = Math.min(lo[i % 3], q.pos[i]); hi[i % 3] = Math.max(hi[i % 3], q.pos[i]); }
  const ext = Math.max(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2], 1e-6);
  const k = Math.min(1, 1 / ext);
  const c = [0, 1, 2].map((a) => (lo[a] + hi[a]) / 2);
  const g = newGeo();
  for (const q of quads) {
    const p: Array<[number, number, number]> = [];
    for (let i = 0; i < 4; i++) p.push([(q.pos[i * 3] - c[0]) * k, (q.pos[i * 3 + 1] - c[1]) * k, (q.pos[i * 3 + 2] - c[2]) * k]);
    const e1 = [p[1][0] - p[0][0], p[1][1] - p[0][1], p[1][2] - p[0][2]];
    const e2 = [p[3][0] - p[0][0], p[3][1] - p[0][1], p[3][2] - p[0][2]];
    const n: [number, number, number] = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    const l = Math.hypot(n[0], n[1], n[2]) || 1;
    n[0] /= l; n[1] /= l; n[2] /= l;
    const uv = [0, 1, 2, 3].map((i) => atlasUv(atlas, q.tile, q.uv[i * 2], q.uv[i * 2 + 1]));
    addQuad(g, p, n, uv);
  }
  return finish(g);
}

// --- model cache -------------------------------------------------------------------------

export interface ItemModel {
  geometry: THREE.BufferGeometry;
  /** True for 3D block models (affects stack-copy jitter and the held transform). */
  gui3d: boolean;
  /** A cube / baked block model held with vanilla's block display (flat tiles and sprites are not). */
  block: boolean;
  /** Vanilla ground display scale (0.5 sprites, 0.25 blocks). */
  groundScale: number;
  /** First-person right hand display transform (ItemTransform): degrees, 1/16 block units, scale. */
  fpRot: readonly [number, number, number];
  fpTrans: readonly [number, number, number];
  fpScale: number;
  texture: THREE.Texture;
  material(kind: 'world' | 'hand'): THREE.ShaderMaterial;
}

/**
 * Vanilla item/handheld firstperson_righthand rotation, shared by every flat
 * sprite. It is tuned for the diagonal sword/pickaxe texture.
 */
const FP_ROT_ITEM: readonly [number, number, number] = [0, -90, 25];
/**
 * Vanilla item/handheld firstperson_righthand translation, in 1/16 block units.
 * Used by the flat sprites.
 */
const FP_TRANS_ITEM: readonly [number, number, number] = [1.13, 3.2, 1.13];
/**
 * Held blocks (tile / model / cube) sit differently from flat items: their
 * geometry is centred on the origin rather than on vanilla's block corner, so
 * they need their own offset. No rightward shift, lifted further.
 */
const FP_TRANS_BLOCK: readonly [number, number, number] = [0, 4.5, 0];
/**
 * The pose a sprite gets when its texture is not drawn on the vanilla
 * generated-item diagonal. The axe and hoe run the opposite diagonal to the
 * sword and pickaxe, and the bow and crossbow are drawn upright and held
 * face-toward the player, so all of them come out mirrored under FP_ROT_ITEM.
 */
const FP_ROT_MIRRORED: readonly [number, number, number] = [0, 90, 25];
const FP_ROT_BY_ID: ReadonlyMap<number, readonly [number, number, number]> = (() => {
  const m = new Map<number, readonly [number, number, number]>();
  const MATS = ['WOODEN', 'STONE', 'IRON', 'GOLDEN', 'DIAMOND', 'NETHERITE', 'COPPER'] as const;
  for (const mat of MATS) {
    for (const tool of ['AXE', 'HOE'] as const) {
      const id = I[`${mat}_${tool}` as ItemName];
      if (typeof id === 'number') m.set(id, FP_ROT_MIRRORED);
    }
  }
  m.set(I.BOW, FP_ROT_MIRRORED);
  m.set(I.CROSSBOW, FP_ROT_MIRRORED);
  return m;
})();

/**
 * Builds and caches one geometry + material per item / block id. Shared between
 * ItemEntityRenderer and FirstPersonHand (so a sprite is built once).
 */
/** In-hand spear scale vs the icon model (was 2: the 32px sprite filled a third of the screen). */
const SPEAR_HAND_SCALE = 1.4;

export class ItemModelCache {
  private readonly models = new Map<string, ItemModel>();
  private readonly pulling: (HTMLImageElement | null)[] = [null, null, null];

  constructor(
    readonly env: EnvUniforms,
    private readonly atlas: AtlasResult,
    private readonly atlasTexture: THREE.Texture,
  ) {
    // bow draw frames (bow_pulling_0..2)
    for (let i = 0; i < 3; i++) {
      const img = new Image();
      img.onload = () => { this.pulling[i] = img; };
      img.src = `mc/item/bow_pulling_${i}.png`;
    }
  }

  /** Model for an item or block id. */
  get(id: number): ItemModel {
    const key = String(id);
    let m = this.models.get(key);
    if (!m) { m = this.build(id); this.models.set(key, m); }
    return m;
  }

  private readonly extraImgs = new Map<string, HTMLImageElement | null>();
  /** Model from an alternate item sprite (crossbow_pulling_1, crossbow_arrow...); `fallbackId` until it loads. */
  getSprite(fallbackId: number, sprite: string): ItemModel {
    const key = `spr:${sprite}`;
    const have = this.models.get(key);
    if (have) return have;
    if (!this.extraImgs.has(sprite)) {
      this.extraImgs.set(sprite, null);
      const img = new Image();
      img.onload = () => { this.extraImgs.set(sprite, img); };
      img.src = `mc/item/${sprite}.png`;
    }
    const img = this.extraImgs.get(sprite);
    if (!img) return this.get(fallbackId);
    const c = document.createElement('canvas');
    c.width = c.height = 16;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, 16, 16);
    const m = this.spriteModel(c);
    const base = this.get(fallbackId);
    Object.assign(m, { fpTrans: base.fpTrans, fpRot: base.fpRot, fpScale: base.fpScale, groundScale: base.groundScale });
    this.models.set(key, m);
    return m;
  }

  private readonly handImgs = new Map<string, HTMLImageElement | null>();
  /**
   * Model held in the hand: spears use the 32x32 `<tier>_spear_in_hand` sprite (twice the icon's length at the
   * same pixel size, so fpScale doubles); every other item is its normal model. Falls back to the icon
   * model scaled up until the image loads.
   */
  getHand(id: number): ItemModel {
    const name = spriteOf(id);
    if (!isSpearId(id) || !name) return this.get(id);
    const key = `hand:${id}`;
    const have = this.models.get(key);
    if (have) return have;
    if (!this.handImgs.has(name)) {
      this.handImgs.set(name, null);
      const img = new Image();
      img.onload = () => { this.handImgs.set(name, img); };
      img.src = `mc/item/${name}_in_hand.png`;
    }
    const img = this.handImgs.get(name);
    const base = this.get(id);
    if (!img) return Object.assign({}, base, { fpScale: base.fpScale * SPEAR_HAND_SCALE });
    const c = document.createElement('canvas');
    c.width = c.height = 32;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, 32, 32);
    const m = this.spriteModel(c);
    Object.assign(m, { fpTrans: base.fpTrans, fpRot: base.fpRot, fpScale: base.fpScale * SPEAR_HAND_SCALE, groundScale: base.groundScale });
    this.models.set(key, m);
    return m;
  }

  /** Bow in draw stage 0..2 (falls back to the idle bow until the frames load). */
  getBow(bowId: number, stage: number): ItemModel {
    const img = this.pulling[Math.max(0, Math.min(2, stage))];
    if (!img) return this.get(bowId);
    const key = `bow${bowId}:${stage}`;
    let m = this.models.get(key);
    if (!m) {
      const c = document.createElement('canvas');
      c.width = c.height = 16;
      const ctx = c.getContext('2d')!;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, 16, 16);
      m = this.spriteModel(c, FP_ROT_BY_ID.get(bowId));
      this.models.set(key, m);
    }
    return m;
  }

  /** Drop every cached model (call after the item icons were (re)loaded). */
  invalidate(): void {
    for (const m of this.models.values()) { m.geometry.dispose(); if (m.texture !== this.atlasTexture) m.texture.dispose(); }
    this.models.clear();
  }

  dispose(): void { this.invalidate(); }

  private build(id: number): ItemModel {
    if (id >= ITEM_BASE) return this.spriteModel(this.itemCanvas(id), FP_ROT_BY_ID.get(id));
    const def = BLOCKS[id];
    if (!def) return this.spriteModel(this.itemCanvas(id));
    const top = DOOR_BOTTOM_TO_TOP[id];
    if (top !== undefined) return this.spriteModel(this.doorCanvas(id, top));
    if (def.shape === RenderShape.Model) return this.blockModel(modelGeometry(id, this.atlas));
    if (def.shape === RenderShape.Cube) return this.blockModel(cubeGeometry(id, this.atlas));
    return this.tileModel(this.tileCanvas(id));
  }

  private itemCanvas(id: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = c.height = 16;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    drawItemIcon(ctx, id, 16);
    return c;
  }

  /** A block drawn flat from its icon tile (flowers, torches, ground cover), tinted like the HUD icon. */
  private tileCanvas(id: number): HTMLCanvasElement {
    const def = BLOCKS[id];
    const tile = def.tiles[2] ?? def.tiles[0];
    const c = document.createElement('canvas');
    c.width = c.height = 16;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    this.blitTile(ctx, tile, 0, 0, 16, 16);
    const tint = iconTint(id, tile);
    if (tint !== null) {
      const img = ctx.getImageData(0, 0, 16, 16);
      const d = img.data;
      const tr = (tint >> 16) & 255, tg = (tint >> 8) & 255, tb = tint & 255;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        d[i] = d[i] * tr / 255; d[i + 1] = d[i + 1] * tg / 255; d[i + 2] = d[i + 2] * tb / 255;
      }
      ctx.putImageData(img, 0, 0);
    }
    return c;
  }

  /** Door item sprite: the two panels stacked into one narrow full-height door. */
  private doorCanvas(bottom: number, top: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = c.height = 16;
    const ctx = c.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    this.blitTile(ctx, BLOCKS[top].tiles[2] ?? BLOCKS[top].tiles[0], 4, 0, 8, 8);
    this.blitTile(ctx, BLOCKS[bottom].tiles[2] ?? BLOCKS[bottom].tiles[0], 4, 8, 8, 8);
    return c;
  }

  private blitTile(ctx: CanvasRenderingContext2D, tile: number, dx: number, dy: number, dw: number, dh: number): void {
    const col = tile % ATLAS_COLS, row = (tile / ATLAS_COLS) | 0;
    ctx.drawImage(this.atlas.canvas, col * CELL + GUTTER, row * CELL + GUTTER, TILE, TILE, dx, dy, dw, dh);
  }

  private spriteModel(canvas: HTMLCanvasElement, fpRot: readonly [number, number, number] = FP_ROT_ITEM): ItemModel {
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    return this.make(spriteGeometry(canvas), tex, false, 0.5, fpRot, FP_TRANS_ITEM, 0.68);
  }

  /** A flat tile held the way a block is held (vanilla `block/block` pose). */
  private tileModel(canvas: HTMLCanvasElement): ItemModel {
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    return this.make(spriteGeometry(canvas), tex, true, 0.5, FP_ROT_ITEM, FP_TRANS_ITEM, 0.68);
  }

  private blockModel(geo: THREE.BufferGeometry): ItemModel {
    return this.make(geo, this.atlasTexture, true, 0.25, [0, 45, 0], FP_TRANS_BLOCK, 0.4, true);
  }
  private make(
    geometry: THREE.BufferGeometry, texture: THREE.Texture, gui3d: boolean, groundScale: number,
    fpRot: readonly [number, number, number], fpTrans: readonly [number, number, number], fpScale: number,
    block = false,
  ): ItemModel {
    const env = this.env;
    const mats: Partial<Record<'world' | 'hand', THREE.ShaderMaterial>> = {};
    return {
      geometry, gui3d, block, groundScale, fpRot, fpTrans, fpScale, texture,
      material(kind) { return (mats[kind] ??= createLitMaterial(env, texture, kind, !gui3d)); },
    };
  }
}

// --- renderer ------------------------------------------------------------------------------

/** ItemRenderer copies: how many stacked sprites a count draws. */
export function renderAmount(count: number): number {
  return count > 48 ? 5 : count > 32 ? 4 : count > 16 ? 3 : count > 1 ? 2 : 1;
}

interface ItemInstance {
  root: THREE.Group;
  meshes: THREE.Mesh[];
  key: string;
}

/** Deterministic 0..1 stream (vanilla seeds the copy offsets from the item id). */
function seeded(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0;
  return () => {
    s = (Math.imul(s ^ (s >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
    return ((s ^ (s >>> 13)) >>> 0) / 4294967296;
  };
}

export class ItemEntityRenderer {
  readonly group = new THREE.Group();
  readonly models: ItemModelCache;

  private readonly items = new Map<number, ItemInstance>();
  private readonly orbMeshes: THREE.Mesh[] = [];
  private readonly orbGeos: THREE.BufferGeometry[] = [];
  private readonly orbMat: THREE.ShaderMaterial;
  private orbTexture: THREE.Texture | null = null;
  private readonly seen = new Set<number>();

  constructor(env: EnvUniforms, atlas: AtlasResult, atlasTexture: THREE.Texture) {
    this.models = new ItemModelCache(env, atlas, atlasTexture);
    this.orbMat = createLitMaterial(env, null, 'orb');
    this.group.frustumCulled = false;
    // the orb sheet moved to entity/experience/ in newer asset dumps; try both
    const loader = new THREE.TextureLoader();
    const load = (url: string, fail: () => void): void => {
      loader.load(url, (t) => {
        t.colorSpace = THREE.SRGBColorSpace;
        t.magFilter = t.minFilter = THREE.NearestFilter;
        t.generateMipmaps = false;
        this.orbTexture = t;
        this.orbMat.uniforms.uMap.value = t;
      }, undefined, fail);
    };
    load('mc/entity/experience/experience_orb.png', () => load('mc/entity/experience_orb.png', () => { /* no orbs */ }));
  }

  update(items: readonly ItemEntityView[], orbs: readonly XpOrbView[], alpha: number, camera: THREE.Camera): void {
    const seen = this.seen;
    seen.clear();
    for (const v of items) {
      if (v.stack.count <= 0 || v.stack.id <= 0) continue;
      seen.add(v.id);
      this.updateItem(v, alpha);
    }
    for (const [id, inst] of this.items) {
      if (seen.has(id)) continue;
      this.group.remove(inst.root);
      this.items.delete(id);
    }
    this.updateOrbs(orbs, alpha, camera);
  }

  private updateItem(v: ItemEntityView, alpha: number): void {
    const model = this.models.get(v.stack.id);
    const amount = renderAmount(v.stack.count);
    const glint = !!v.stack.ench?.length;
    const key = `${v.stack.id}:${amount}:${glint ? 1 : 0}`;
    let inst = this.items.get(v.id);
    if (inst && inst.key !== key) { this.group.remove(inst.root); inst = undefined; }
    if (!inst) {
      const root = new THREE.Group();
      const meshes: THREE.Mesh[] = [];
      const mat = model.material('world');
      const rnd = seeded(v.stack.id + 1);
      for (let i = 0; i < amount; i++) {
        const mesh = new THREE.Mesh(model.geometry, mat);
        mesh.frustumCulled = false;
        mesh.scale.setScalar(model.groundScale);
        // copies jitter in item space (vanilla: +-0.15 for 3D models, half that and flat for sprites)
        if (i > 0) {
          const k = model.gui3d ? 0.15 : 0.075;
          mesh.position.set((rnd() * 2 - 1) * k, (rnd() * 2 - 1) * k, model.gui3d ? (rnd() * 2 - 1) * k : 0);
        }
        if (glint) syncGlint(mesh, true);
        root.add(mesh);
        meshes.push(mesh);
      }
      this.group.add(root);
      inst = { root, meshes, key };
      this.items.set(v.id, inst);
    }
    const t = alpha;
    const x = v.px + (v.x - v.px) * t, y = v.py + (v.y - v.py) * t, z = v.pz + (v.z - v.pz) * t;
    const bobOffs = (((v.id * 2654435761) >>> 0) / 4294967296) * Math.PI * 2;
    const age = v.age + alpha;
    const bob = Math.sin(age / 10 + bobOffs) * 0.1 + 0.1;
    // vanilla centres the model: 0.25 * groundScale up, plus the display translation (2/16 sprites, 3/16 blocks)
    const centre = model.groundScale * 0.25 + (model.gui3d ? 3 : 2) / 16;
    inst.root.position.set(x, y + bob + centre, z);
    inst.root.rotation.y = age / 20 + bobOffs;
    for (const m of inst.meshes) setMeshLight(m, v.light);
  }

  private orbGeometry(icon: number): THREE.BufferGeometry {
    let g = this.orbGeos[icon];
    if (!g) {
      const geo = newGeo();
      const u0 = (icon % 4) * 0.25, u1 = u0 + 0.25;
      const v1 = 1 - (icon >> 2) * 0.25, v0 = v1 - 0.25;
      addQuad(geo, [[-0.5, -0.25, 0], [0.5, -0.25, 0], [0.5, 0.75, 0], [-0.5, 0.75, 0]], [0, 0, 1], [[u0, v0], [u1, v0], [u1, v1], [u0, v1]]);
      g = this.orbGeos[icon] = finish(geo);
    }
    return g;
  }

  private updateOrbs(orbs: readonly XpOrbView[], alpha: number, camera: THREE.Camera): void {
    while (this.orbMeshes.length < orbs.length) {
      const m = new THREE.Mesh(this.orbGeometry(0), this.orbMat);
      m.frustumCulled = false;
      m.scale.setScalar(0.3);
      this.group.add(m);
      this.orbMeshes.push(m);
    }
    camera.updateWorldMatrix(true, false);
    const q = camera.getWorldQuaternion(new THREE.Quaternion());
    for (let i = 0; i < this.orbMeshes.length; i++) {
      const m = this.orbMeshes[i];
      if (i >= orbs.length || !this.orbTexture) { m.visible = false; continue; }
      const o = orbs[i];
      m.visible = true;
      m.geometry = this.orbGeometry(orbIconFor(o.value));
      m.position.set(
        o.px + (o.x - o.px) * alpha, o.py + (o.y - o.py) * alpha + 0.1, o.pz + (o.z - o.pz) * alpha,
      );
      m.quaternion.copy(q);
      // vanilla pulse: red swings 0..1, green full, blue 0..0.2, multiplied in sRGB space
      const f = (o.age + alpha) / 2;
      const r = (Math.sin(f) + 1) * 0.5, b = (Math.sin(f + 4.1887903) + 1) * 0.1;
      setMeshLight(m, Math.max(o.light, 0.5), Math.pow(r, 2.2), 1, Math.pow(b, 2.2));
    }
  }

  dispose(): void {
    this.models.dispose();
    for (const g of this.orbGeos) g?.dispose();
    this.orbMat.dispose();
    this.orbTexture?.dispose();
  }
}
